import { ExIcon, ExMenuItem, MenuItemVariant } from '@boomi/exosphere';
import { useEffect, useRef } from 'react';
import { signOut } from '../okta';
import { translations } from '../translations/translations';
import type { Notification } from '../types/types';
import { base64JpgToSrc } from '../utils/utils';
import AnimatedLink from './AnimatedLink';
import { useApp } from './AppProvider';
import BoomiNavigationMenu from './BoomiNavigationMenu';
import BoomiNavigationMenuMobile from './BoomiNavigationMenuMobile';

import './header.css';
import './boomi-navigation-menu.css';

const TaskAutomationHeader = () => {
    const mobileMenuWrapper = useRef<HTMLDetailsElement>(null);
    const {
        userInfo,
        language,
        notifications,
        hasUnreadNotifications,
        setHasUnreadNotifications,
        currentUserProfileImage,
        unsavedChanges,
        setUnsavedChangesNavigateTo,
    } = useApp();
    const username = `${userInfo?.['given_name'] ?? ''} ${userInfo?.['family_name'] ?? ''}`;

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        const checkForUnreadNotifications = (notifications: Notification[]) => {
            const foundUnread = notifications.some((notification) => {
                if (!notification.read) {
                    setHasUnreadNotifications(true);
                    return true;
                }
                return false;
            });

            if (!foundUnread) {
                setHasUnreadNotifications(false);
            }
        };
        checkForUnreadNotifications(notifications);
    }, [hasUnreadNotifications, notifications, setHasUnreadNotifications]);

    useEffect(() => {
        const handleMobileMenuOutsideClicks = (event: MouseEvent) => {
            if (
                (mobileMenuWrapper && !mobileMenuWrapper.current?.contains(event.target as Node)) ||
                (event.target as Element).classList.contains('menu-item')
            ) {
                mobileMenuWrapper?.current?.removeAttribute('open');
            }
        };
        document.addEventListener('click', handleMobileMenuOutsideClicks);

        return () => {
            document.removeEventListener('click', handleMobileMenuOutsideClicks);
        };
    }, []);

    const numberOfUnread = () => {
        const unread = notifications.filter((n) => !n.read).length;
        return unread > 9 ? '9+' : unread;
    };

    const isLinkActive = (linkLocation: string): boolean =>
        window.location.pathname.endsWith(linkLocation);

    return (
        <header className="automate-header">
            {/* DESKTOP HEADER */}
            <div className="automate-header-desktop">
                <img className="boomi-logo" src="/img/b-mark.svg" alt="Boomi" />

                <AnimatedLink className="product-logo" to="/tasks">
                    <span className="product-name">{translations[language].AUTOMATE_title}</span>
                </AnimatedLink>

                <nav className="automate-navigation">
                    <AnimatedLink
                        className={`automate-link ${isLinkActive('/tasks') ? 'is-active' : ''}`}
                        to="/tasks"
                    >
                        {translations[language].USER_MENU_home}
                    </AnimatedLink>
                    <AnimatedLink
                        className={`automate-link ${
                            isLinkActive('/connections') ? 'is-active' : ''
                        }`}
                        to="/connections"
                    >
                        {translations[language].USER_MENU_connections}
                    </AnimatedLink>
                    <AnimatedLink
                        className={`automate-link ${
                            isLinkActive('/notifications') ? 'is-active' : ''
                        }`}
                        to="/notifications"
                    >
                        {translations[language].USER_MENU_notifications}
                        {hasUnreadNotifications && (
                            <span className="notifications-counter">{numberOfUnread()}</span>
                        )}
                    </AnimatedLink>
                </nav>

                <section className="automate-utilities">
                    <a
                        className="automate-link"
                        href="https://help.boomi.com/"
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        <ExIcon icon="Help" className="utility-icon" hide-browser-tooltip />
                    </a>

                    <BoomiNavigationMenu />

                    <AnimatedLink className="automate-link user-button" to="/profile">
                        {currentUserProfileImage ? (
                            <img
                                className="user-avatar"
                                src={base64JpgToSrc(currentUserProfileImage)}
                                alt="User avatar"
                            />
                        ) : (
                            <ExIcon
                                className="user-avatar-blank"
                                icon="User"
                                hide-browser-tooltip
                            />
                        )}
                        <span className="user-name">{username}</span>
                    </AnimatedLink>

                    <button
                        className="automate-link logout-button"
                        onClick={() => {
                            if (unsavedChanges) {
                                setUnsavedChangesNavigateTo('signOut');
                                return;
                            }
                            signOut();
                        }}
                        type="button"
                    >
                        {translations[language].USER_MENU_sign_out}
                    </button>
                </section>
            </div>

            {/* MOBILE HEADER */}
            <div className="automate-header-mobile">
                <img className="boomi-logo" src="/img/b-mark.svg" alt="Boomi" />

                <AnimatedLink className="product-logo" to="/tasks">
                    <span className="product-name">{translations[language].AUTOMATE_title}</span>
                </AnimatedLink>

                <details className="header-mobile-menu" ref={mobileMenuWrapper}>
                    <summary className="header-mobile-menu-trigger automate-link">
                        <ExIcon className="utility-icon" icon="More options" hide-browser-tooltip />
                    </summary>

                    <div className="header-mobile-menu-content custom-scrollbars">
                        <div className="user-panel">
                            <AnimatedLink
                                className="automate-button-mobile user-button-mobile"
                                to="/profile"
                            >
                                {currentUserProfileImage ? (
                                    <img
                                        className="user-avatar"
                                        src={base64JpgToSrc(currentUserProfileImage)}
                                        alt="User avatar"
                                    />
                                ) : (
                                    <ExIcon
                                        className="user-avatar-blank"
                                        icon="User"
                                        hide-browser-tooltip
                                    />
                                )}
                                <span className="user-name">{username}</span>
                            </AnimatedLink>

                            <button
                                className="automate-button-mobile logout-button-mobile"
                                // biome-ignore lint/a11y/useSemanticElements: Requires refactor
                                role="link"
                                onClick={() => {
                                    if (unsavedChanges) {
                                        setUnsavedChangesNavigateTo('signOut');
                                        return;
                                    }
                                    signOut();
                                }}
                                type="button"
                            >
                                {translations[language].USER_MENU_sign_out}
                            </button>
                        </div>

                        <AnimatedLink
                            className={`automate-link-mobile ${
                                isLinkActive('/tasks') ? 'is-active' : ''
                            }`}
                            to="/tasks"
                        >
                            <ExMenuItem className="menu-item" variant={MenuItemVariant.NAVIGATION}>
                                {translations[language].USER_MENU_home}
                            </ExMenuItem>
                        </AnimatedLink>
                        <AnimatedLink
                            className={`automate-link-mobile ${
                                isLinkActive('/connections') ? 'is-active' : ''
                            }`}
                            to="/connections"
                        >
                            <ExMenuItem className="menu-item" variant={MenuItemVariant.NAVIGATION}>
                                {translations[language].USER_MENU_connections}
                            </ExMenuItem>
                        </AnimatedLink>
                        <AnimatedLink
                            className={`automate-link-mobile ${
                                isLinkActive('/notifications') ? 'is-active' : ''
                            }`}
                            to="/notifications"
                        >
                            <ExMenuItem className="menu-item" variant={MenuItemVariant.NAVIGATION}>
                                {translations[language].USER_MENU_notifications}
                                {hasUnreadNotifications && (
                                    <span className="notifications-counter">
                                        {numberOfUnread()}
                                    </span>
                                )}
                            </ExMenuItem>
                        </AnimatedLink>

                        <BoomiNavigationMenuMobile />
                    </div>
                </details>
            </div>
        </header>
    );
};

export default TaskAutomationHeader;
